import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Typography,
  Avatar,
  InputAdornment,
  Divider,
  Card,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Autorenew as AutorenewIcon,
  Business as BusinessIcon,
  AttachMoney as AttachMoneyIcon,
  CalendarToday as CalendarTodayIcon,
  Notes as NotesIcon,
  Person as PersonIcon,
  AttachFile as AttachFileIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import { renewBrokerSystem } from "../../../Services/BrokersApi";
import { themeColors } from "../../../theme/colors";

const RenewSystemDialog = ({ open, tenantId, onClose, onRenewed }) => {
  const [cost, setCost] = useState("");
  const [renewDate, setRenewDate] = useState("");
  const [notes, setNotes] = useState("");
  const [employeeName, setEmployeeName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      // إعادة تهيئة عند إغلاق الحوار
      setCost("");
      setRenewDate("");
      setNotes("");
      setEmployeeName("");
      setStartDate("");
      setEndDate("");
      setFile(null);
      setError(null);
    }
  }, [open]);

  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
  };

  const handleSubmit = async () => {
    if (!tenantId) {
      setError("tenant_id مطلوب");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("authToken");
      const payload = {
        tenant_id: tenantId,
        cost: cost || undefined,
        renew_date: renewDate || undefined,
        notes: notes || undefined,
        employee_name: employeeName || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        file: file || undefined,
      };
      await renewBrokerSystem(payload, token);
      if (onRenewed) onRenewed();
    } catch (err) {
      setError((err && (err.message || err.msg || JSON.stringify(err))) || "فشل تجديد النظام");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          pb: 2,
          background: `linear-gradient(135deg, ${themeColors.info} 0%, #1976D2 100%)`,
          color: "white",
          position: "relative",
        }}
      >
        <Avatar
          sx={{
            width: 48,
            height: 48,
            bgcolor: "rgba(255,255,255,0.25)",
            backdropFilter: "blur(10px)",
          }}
        >
          <AutorenewIcon />
        </Avatar>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            تجديد النظام
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            تجديد الاشتراك وتحديث بيانات النسخة
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            left: 8,
            top: 8,
            color: "white",
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.15)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(220, 53, 69, 0.15)",
            }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Basic Info Section */}
        <Card
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${themeColors.border.light}`,
            bgcolor: themeColors.background.default,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, mb: 2.5, color: themeColors.text.primary }}
          >
            📋 المعلومات الأساسية
          </Typography>

          <Box display="flex" flexDirection="column" gap={2.5}>
            <TextField
              label="معرّف النسخة"
              value={tenantId ?? ""}
              disabled
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon sx={{ color: themeColors.text.secondary }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: themeColors.background.hover,
                },
              }}
            />

            <Box display="flex" gap={2}>
              <TextField
                label="التكلفة"
                placeholder="0.00"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon sx={{ color: themeColors.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: themeColors.info,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: themeColors.info,
                    },
                  },
                }}
              />

              <TextField
                label="تاريخ التجديد"
                type="date"
                value={renewDate}
                onChange={(e) => setRenewDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayIcon sx={{ color: themeColors.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: themeColors.info,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: themeColors.info,
                    },
                  },
                }}
              />
            </Box>

            <TextField
              label="اسم الموظف"
              placeholder="اسم الموظف المسؤول"
              value={employeeName}
              onChange={(e) => setEmployeeName(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: themeColors.text.secondary }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover fieldset": {
                    borderColor: themeColors.info,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: themeColors.info,
                  },
                },
              }}
            />

            <TextField
              label="ملاحظات"
              placeholder="أضف أي ملاحظات إضافية..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={3}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1 }}>
                    <NotesIcon sx={{ color: themeColors.text.secondary }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover fieldset": {
                    borderColor: themeColors.info,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: themeColors.info,
                  },
                },
              }}
            />
          </Box>
        </Card>

        {/* Dates Section */}
        <Card
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${themeColors.border.light}`,
            bgcolor: themeColors.background.default,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, mb: 2.5, color: themeColors.text.primary }}
          >
            📅 فترة الاشتراك
          </Typography>

          <Box display="flex" gap={2}>
            <TextField
              label="تاريخ البداية"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarTodayIcon sx={{ color: themeColors.text.secondary }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover fieldset": {
                    borderColor: themeColors.info,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: themeColors.info,
                  },
                },
              }}
            />

            <TextField
              label="تاريخ النهاية"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarTodayIcon sx={{ color: themeColors.text.secondary }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover fieldset": {
                    borderColor: themeColors.info,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: themeColors.info,
                  },
                },
              }}
            />
          </Box>
        </Card>

        {/* File Upload Section */}
        <Card
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 2,
            border: `1px solid ${themeColors.border.light}`,
            bgcolor: themeColors.background.default,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, mb: 2.5, color: themeColors.text.primary }}
          >
            📎 المرفقات
          </Typography>

          <Button
            variant="outlined"
            component="label"
            fullWidth
            startIcon={<CloudUploadIcon />}
            sx={{
              py: 2,
              borderRadius: 2,
              borderStyle: "dashed",
              borderWidth: 2,
              borderColor: themeColors.info,
              color: themeColors.info,
              "&:hover": {
                borderColor: "#1976D2",
                bgcolor: `${themeColors.info}08`,
              },
            }}
          >
            {file ? "تغيير الملف" : "رفع ملف"}
            <input type="file" hidden onChange={handleFileChange} />
          </Button>

          {file && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: `${themeColors.success}08`,
                borderRadius: 2,
                border: `1px solid ${themeColors.success}30`,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <AttachFileIcon sx={{ color: themeColors.success, fontSize: 20 }} />
              <Typography variant="body2" sx={{ flex: 1, color: themeColors.text.primary }}>
                {file.name}
              </Typography>
              <Chip
                label={`${(file.size / 1024).toFixed(2)} KB`}
                size="small"
                sx={{
                  bgcolor: themeColors.success,
                  color: "white",
                  fontWeight: 600,
                }}
              />
            </Box>
          )}
        </Card>

        <Box
          sx={{
            mt: 3,
            p: 2,
            bgcolor: `${themeColors.info}08`,
            borderRadius: 2,
            border: `1px solid ${themeColors.info}30`,
          }}
        >
          <Typography variant="caption" sx={{ color: themeColors.text.secondary }}>
            🔄 <strong>ملاحظة:</strong> سيتم تجديد النسخة بالبيانات المحددة وتحديث تاريخ انتهاء الاشتراك
          </Typography>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          sx={{
            borderRadius: 2,
            px: 3,
            borderColor: themeColors.border.main,
            color: themeColors.text.secondary,
            "&:hover": {
              borderColor: themeColors.text.primary,
              bgcolor: "transparent",
            },
          }}
        >
          إلغاء
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{
            borderRadius: 2,
            px: 4,
            bgcolor: themeColors.info,
            boxShadow: `0 4px 12px ${themeColors.info}40`,
            "&:hover": {
              bgcolor: "#1976D2",
              boxShadow: `0 6px 16px ${themeColors.info}60`,
            },
            "&:disabled": {
              bgcolor: themeColors.text.light,
            },
          }}
        >
          {loading ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} color="inherit" />
              <span>جاري التجديد...</span>
            </Box>
          ) : (
            <>
              <AutorenewIcon sx={{ mr: 1, fontSize: 20 }} />
              تجديد النظام
            </>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RenewSystemDialog;
